import { useEffect, useMemo, useRef } from "react";
import {
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Paper,
    useTheme,
    Grid,
    Badge,
    styled,
    CardMedia,
} from "@mui/material";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import posImg from "../../assets/pos.jpg";
import { useLocation, useNavigate } from "react-router-dom";
import { allowOrWarn } from '../../util/Util';
import { LocalStorageKeys } from "../../util/constants";
import { pendingTaskCountsBySource } from "../../util/pendingTaskStorage";
import { canRecord } from '../../util/access';

const FloatingBadge = styled(Badge)(({ theme }) => ({
    position: "absolute",
    top: -10,
    right: -10,
    "& .MuiBadge-badge": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
        transform: "translate(25%, -25%)",
        borderRadius: "50%",
        height: 32,
        minWidth: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
    },
}));

const POSGrid = () => {
    const { api, session, access } = useAuth();
    const { setLoadingMessage, queueReRun, setActiveDepot, t } = useUI();
    const { data, saveFromServer } = useData();
    const theme = useTheme();

    const toastIdRef = useRef<string | number>(-1);

    const location = useLocation();
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoadingMessage(t("common:pos.loadingTerminals"));

        const res = await api.get(`depot/sales/`);

        if (res) {
            if (res.status === 200) {
                saveFromServer(res.data);
                toast.update(toastIdRef.current, {
                    render: t("common:toast.success.refresh"),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                });
            } else if (res.status === 401 || res.status === 422) {
                queueReRun(fetchData);
            } else {
                console.error("Failed to fetch data:", res);
                toast.dismiss(toastIdRef.current);
            }
        } else {
            toast.dismiss(toastIdRef.current);
        }

        setLoadingMessage(null);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const pendingBySource = useMemo(
        () => pendingTaskCountsBySource(LocalStorageKeys.SALE_TASKS, session?.user_id),
        [session?.user_id],
    );

    const handleCardSelect = (pos: any) => {
        if (allowOrWarn(canRecord(access, 'sale'))) {
            setActiveDepot(pos.id);
            navigate("/pos");
        }
    }

    return (
        <Paper
            elevation={3}
            sx={{
                p: 3,
                borderRadius: 3,
                minHeight: "70vh",
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                width={"100%"}
                align="center"
            >
                {t("common:pos.availableTerminals")}
            </Typography>

            <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
                {data.map((pos: any) => (
                    <Grid
                        key={pos.id}
                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                        sx={{ display: "flex", justifyContent: "center" }}
                    >
                        <Card
                            sx={{
                                width: "100%",
                                borderRadius: 4,
                                overflow: "visible",
                                bgcolor: theme.palette.background.paper,
                                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                position: "relative",
                                "&:hover": {
                                    transform: "translateY(-6px)",
                                    boxShadow: theme.shadows[6],
                                },
                            }}
                        >
                            <CardActionArea
                                onClick={() => handleCardSelect(pos)}
                                sx={{ position: "relative" }}
                            >
                                <FloatingBadge
                                    badgeContent={pendingBySource.get(Number(pos.id)) || 0}
                                    max={9}
                                    showZero
                                >
                                    {/* <PendingActionsIcon
                                    /> */}
                                </FloatingBadge>

                                <CardMedia
                                    component="img"
                                    height="160"
                                    image={pos.image || posImg}
                                    alt={pos.DepotName}
                                    sx={{
                                        objectFit: "cover",
                                        borderTopLeftRadius: "16px",
                                        borderTopRightRadius: "16px",
                                    }}
                                />

                                <CardContent
                                    sx={{
                                        textAlign: "center",
                                        px: 2,
                                        py: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                        sx={{
                                            textTransform: "capitalize",
                                            mb: 0.5,
                                        }}
                                    >
                                        {pos.DepotName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.4,
                                            minHeight: 40,
                                        }}
                                    >
                                        {pos.Description}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

export default POSGrid;
